import React, { useEffect, useCallback, useState } from "react";
import _ from "lodash";
import { connect } from "react-redux";
import DeviceSelector from "./DeviceSelector";
import { useWebServer } from "../../../providers";
import { DEFAULT_DATA_SETTINGS } from "../../../reducers/settingsReducer";
import {
  selectDevice,
  fetchTrays,
  fetchOrganizationFilters,
  clearTrays,
  displayOverwritableWarningNotification,
  displayPermanentWarningNotification,
  getDeviceNetworkMetrics,
  clearGeneralFilters,
  updateUserSelectedDevice,
  updateDataTableSettings
} from "./../../../actions";
import { phraseDownloadDataFilter } from "../../../utils/dataDownload";
import { compareVersions, parseVersion } from "../../../utils/versions";
import { RTT_AVG_MAX, NETWORK_DROP_PERC_MAX } from "../../../utils/const";
import { useHistoryStack } from "../../../providers/HistoryProvider";

const defaultTraySettings = _.pick(DEFAULT_DATA_SETTINGS.Tray, [
  "page",
  "pageSize",
  "order",
  "orderBy",
]);

const Index = ({
  selectDevice,
  // getDeviceNetworkMetrics,
  fetchTrays,
  fetchOrganizationFilters,
  displayPermanentWarningNotification,
  displayOverwritableWarningNotification,
  selectedDevice,
  devices,
  devicesLength,
  deviceMetrics,
  deviceMetricsLength,
  isLoading,
  disabled,
  queryParams,
  generalFilters,
  timeZone,
  clearTrays,
  clearGeneralFilters,
  updateUserSelectedDevice,
  userDefaultDevice,
  isTrayFilterModalOpen,
  updateDataTableSettings
}) => {
  const { sendRequest } = useWebServer();
  const historyStack = useHistoryStack();
  const [isFirstFetch, setIsFirstFetch] = useState(false)

  useEffect(() => {
    const indexReportFund = historyStack.findIndex((cur) => {
      return cur.includes("report");
    });
    if (
      historyStack
        .slice(0, indexReportFund)
        ?.filter((cur) => cur.includes("tray"))?.length === 1
    ) {
      clearGeneralFilters();
    }
  }, [historyStack, clearGeneralFilters]);

  useEffect(() => {
    fetchOrganizationFilters(sendRequest);
  }, [fetchOrganizationFilters, sendRequest]);

  const debouncedFetchTrays = React.useMemo(
    () =>
      _.debounce(
        (generalFilters, timeZone, selectedDevice) => {
          let filters = phraseDownloadDataFilter(generalFilters, timeZone);
          if (selectedDevice) {
            //clear all tray on the tool
            clearTrays(selectedDevice);
            fetchTrays(
              { ...defaultTraySettings, ...filters },
              sendRequest,
              selectedDevice
            );
          }
        },
        1000,
        { leading: false, trailing: true }
      ),
    [clearTrays, fetchTrays, sendRequest]
  );

  useEffect(() => {
    return () => debouncedFetchTrays.cancel();
  }, [debouncedFetchTrays]);

  const debounceFetchTrays = useCallback(
    (generalFilters, timeZone, selectedDevice) => {
      debouncedFetchTrays(generalFilters, timeZone, selectedDevice);
    },
    [debouncedFetchTrays]
  );

  useEffect(() => {
    if (!isTrayFilterModalOpen) {
      return;
    }
    if (selectedDevice && selectedDevice in devices) {
      debounceFetchTrays(generalFilters, timeZone, selectedDevice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice, generalFilters]);

  useEffect(() => {
    if (Object.values(devices).length > 0) {
      // run getDeviceNetworkMetrics on all IDLE devices
      // Object.values(devices).forEach(({ stateMachine, id }) => {
      //   if (stateMachine === "IDLE") {
      //     getDeviceNetworkMetrics(id);
      //   }
      // });

      // low version devices
      let lowVersionDevices = Object.values(devices).reduce((arr, d) => {
        if (
          !(
            (typeof d?.algVersion === "string" ||
              d?.algVersion instanceof String) &&
            !d?.retired
          )
        )
          return arr;
        // an unparseable algVersion is unknown, not behind - compareVersions
        // sorts those below every real version, so the check has to be explicit
        if (
          parseVersion(d.algVersion) &&
          compareVersions(d.algVersion, "v5.0.0") === -1
        ) {
          return [...arr, d.displayName];
        }
        return arr;
      }, []);
      console.log(
        `Found ${lowVersionDevices.length
        } low version devices out of a possible ${Object.values(devices).length - 1
        }`
      );
      const numLVDevices = lowVersionDevices.length;
      if (lowVersionDevices.length >= 5) {
        lowVersionDevices = [
          ...lowVersionDevices.slice(0, 4),
          `${numLVDevices - 4} more devices`,
        ];
      }
      if (lowVersionDevices.length > 0) {
        displayPermanentWarningNotification(
          `You have ${numLVDevices} devices (${lowVersionDevices.join(
            ", "
          )}) that are in need of an update. Until these devices are updated, empty detection and sex classification may not work correctly for new captures.`,
          "lowVersionDevicesWarning"
        );
      }
    } else {
      console.log(`devices.length: ${Object.values(devices).length}`);
    }
    // eslint-disable-next-line
  }, [
    devicesLength,
    displayPermanentWarningNotification,
    getDeviceNetworkMetrics,
  ]);

  // when there are new deviceMetrics, update the warning notification
  useEffect(() => {
    if (deviceMetricsLength > 0) {
      let lowSpeedDevices = Object.keys(deviceMetrics);
      if (lowSpeedDevices.length >= 5) {
        lowSpeedDevices = [
          ...lowSpeedDevices.slice(0, 4),
          `${lowSpeedDevices.length - 4} more devices`,
        ];
      }
      displayOverwritableWarningNotification(
        "metrics",
        `You have ${deviceMetricsLength} devices (${lowSpeedDevices.join(
          ", "
        )}) that may have a poor internet connection.`
      );
      Object.entries(deviceMetrics).forEach(([deviceUuid, message]) => {
        console.log(`[${deviceUuid}]: ${message}`);
      });
    }
    // eslint-disable-next-line
  }, [deviceMetricsLength, displayOverwritableWarningNotification]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const patchUserSelectedDevice = useCallback(
    _.debounce(async (selectedDevice) => {
      try {
        await sendRequest("patch")({
          url: `/user/user-selected-device`,
          devPort: 5004,
          data: { selectedDevice: selectedDevice },
        });
      } catch (err) {
        console.log(err);
      }
    }, 3000), // 3000ms debounce delay
    []
  );

  //handle user default selected device loading
  useEffect(() => {
    if (isFirstFetch) {
      return
    }
    if (userDefaultDevice !== selectedDevice && !!devices) {
      const toolId = devices?.[userDefaultDevice]?.id;
      if (toolId) {
        selectDevice(userDefaultDevice)
        fetchTrays(queryParams, sendRequest, toolId);
        setIsFirstFetch(true)
      }
    }
    //eslint-disable-next-line
  }, [userDefaultDevice, selectDevice, devices, isFirstFetch])


  const onChangeDevice = (obj) => {
    const selectedDevice = obj?.target?.value;
    if (typeof selectedDevice === "string") {
      selectDevice(selectedDevice);
      updateUserSelectedDevice(selectedDevice);
      patchUserSelectedDevice(selectedDevice);
      const toolId = devices?.[selectedDevice]?.id;
      // don't need to fetch when it is on the modal 
      if (toolId && !isTrayFilterModalOpen) {
        updateDataTableSettings('Tray', { page: 0 })
        fetchTrays({ ...queryParams, page: 0 }, sendRequest, toolId);
      }
    }
  };

  return (
    <DeviceSelector
      devices={devices}
      selectedDevice={selectedDevice ? selectedDevice : "none"}
      handleChangeDevice={onChangeDevice}
      loading={isLoading}
      disabled={disabled}
    ></DeviceSelector>
  );
};

export const mapStateToProps = (state) => {
  const { selectedDevice, ...devices } = state.devices;
  const { capturing = false } = state.capture;
  const { confirmed: licenseConfirmed = "UNKNOWN" } = state.license;
  const { status: wifiSetupStatus } = state.wifi;
  const { status: wsStatus } = state.websocket;
  const { fetchingMacAddressStatus } = state.devices;
  const queryParams = _.pick(state.settings.Tray, [
    "page",
    "pageSize",
    "order",
    "orderBy",
  ]);
  const disabled =
    wsStatus !== "open" ||
    capturing ||
    licenseConfirmed === "PENDING" ||
    !(wifiSetupStatus === "disconnected" || wifiSetupStatus === "ready") ||
    !(
      fetchingMacAddressStatus === "disconnected" ||
      fetchingMacAddressStatus === "ready"
    );
  const isLoading =
    wsStatus !== "open" || !devices || Object.keys(devices).length === 0;

  const generalFilters = state.data?.generalFilters || {};
  const { timeZone } = state.user;
  const devicesObj = _.pickBy(
    devices,
    (value, _) =>
      value?.env === process.env.REACT_APP_ENV.toUpperCase() ||
      value?.env === "ALLTRAY"
  );
  // collect devices for which networkMetrics indicate a poor connection
  const deviceMetrics = Object.values(devicesObj).reduce((arr, device) => {
    if (device?.networkMetrics?.rttAvg) {
      let slow = false;
      let unstable = false;
      if (device.networkMetrics.rttAvg > RTT_AVG_MAX) {
        slow = true;
      }
      if (device.networkMetrics.dropPerc > NETWORK_DROP_PERC_MAX) {
        unstable = true;
      }
      if (slow || unstable) {
        const problem =
          slow && unstable ? "slow and unstable" : slow ? "slow" : "unstable";
        return {
          ...arr,
          [device.id]: `${device.displayName} may have a ${problem} network connection.`,
        };
      }
    }
    return arr;
  }, {});

  return {
    devices: devicesObj,
    devicesLength: Object.values(devicesObj).length,
    deviceMetrics,
    deviceMetricsLength: Object.values(deviceMetrics).length,
    isLoading,
    selectedDevice,
    disabled,
    queryParams,
    generalFilters,
    timeZone,
    isTrayFilterModalOpen: state.data.isTrayModalOpen,
    userDefaultDevice: state.user.selectedDevice
  };
};

export default connect(mapStateToProps, {
  selectDevice,
  fetchTrays,
  fetchOrganizationFilters,
  clearTrays,
  displayOverwritableWarningNotification,
  displayPermanentWarningNotification,
  getDeviceNetworkMetrics,
  clearGeneralFilters,
  updateUserSelectedDevice,
  updateDataTableSettings
})(Index);
